import React from 'react';
import DialogMUI from '../../shared/DialogMUI';
import ButtonMUI from '../../shared/ButtonMUI';
import {closeModal} from '../../redux/actions/app/appActions';
import {useDispatch, useSelector} from 'react-redux';
import Dropzone from '../Components/Dropzone';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import './Support.scss';
import InputMUI from '../../shared/InputMUI';
import {sendSupportMessage} from '../../redux/actions/support/supportActions';
import {notifyError, notifySuccess} from '../../helpers/notifySnack';
import {POPUPS} from '../../redux/actions/app/appActionTypes';
import {useTranslation} from 'react-i18next';

const SupportPopup = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const popupToShow = useSelector(({app}) => app.popupToShow);

  const schema = yup.object({
    subject: yup.string().required(t('validations.required')),
    description: yup.string().required(t('validations.required'))
  });

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: {errors, isValid, isSubmitting}
  } = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: yupResolver(schema),
    defaultValues: {
      subject: '',
      description: '',
      files: []
    }
  });

  const onCloseHandler = () => {
    dispatch(closeModal());
    reset({
      subject: '',
      description: '',
      files: []
    });
  };

  const onFilesAdded = (files) => {
    setValue('files', files, {shouldValidate: false});
  };

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();
      formData.append('subject', data.subject);
      formData.append('message', data.description);

      if (data.files.length) {
        data.files.forEach((file) => formData.append(`file`, file));
      }

      const res = await dispatch(sendSupportMessage(formData));

      if (res.payload && res.payload.status && res.payload.status === 202) {
        notifySuccess(t('all.successfully'));
        onCloseHandler();
      } else {
        const errors = res.error.response.data.errors;
        if (errors) {
          notifyError(
            Object.values(errors)
              .map((item) => item)
              .join(';\n') ?? t('all.error')
          );
        } else {
          const message = res.error.response.data.message;
          notifyError(message ?? t('all.error'));
        }
      }
    } catch (err) {
      notifyError(t('SupportPopup.error'));
    }
  };

  const popupVisible = popupToShow === POPUPS.SUPPORT_POPUP;

  return (
    <div className='mailing_popup'>
      <DialogMUI maxWidth={'sm'} open={popupVisible} onClose={onCloseHandler} title={t('SupportPopup.title')}>
        <div className='mailing__header'>{t('SupportPopup.header')}</div>
        <div className='mailing__input'>
          <Controller
            name='subject'
            control={control}
            render={({field}) => (
              <InputMUI
                type='text'
                fullWidth
                className='no-icons'
                error={errors.subject?.message}
                inputProps={field}
                label={t('SupportPopup.subjectLabel')}
              />
            )}
          />
        </div>

        <div className='mailing__textarea'>
          <div className='mailing__name'>{t('SupportPopup.descriptionLabel')}</div>
          <Controller
            name='description'
            control={control}
            render={({field}) => (
              <InputMUI
                className='no-icons'
                type='text'
                fullWidth
                error={errors.description?.message}
                inputProps={field}
                multiline={true}
                placeholder={t('SupportPopup.placeholder')}
                minRows={3.3}
                maxRows={4}
              />
            )}
          />
        </div>
        <div className='mailing__dropzone'>
          <Dropzone maxFiles={1} maxSizeMb={50} onFilesAdded={onFilesAdded} />
        </div>
        <ButtonMUI
          onClick={handleSubmit(onSubmit)}
          fullWidth={true}
          disabled={!isValid}
          loading={isSubmitting}
          formAction
        >
          {t('SupportPopup.submitButton')}
        </ButtonMUI>
      </DialogMUI>
    </div>
  );
};

export default SupportPopup;
